<template>
  <v-container fluid class="plant-planner">
    <div class="planner-top">
      <div>
        <h1 class="planner-title">Planificador de planta</h1>
        <p class="planner-subtitle">Carga por máquina, secuencia y fecha estimada según capacidad</p>
      </div>
      <div class="planner-actions">
        <v-btn outlined color="primary" :disabled="loading" @click="fetchPlant">
          <v-icon left small>fas fa-sync-alt</v-icon>
          Recargar
        </v-btn>
        <v-btn color="primary" :disabled="loading || !dirty" :loading="saving" @click="savePlant">
          <v-icon left small>fas fa-save</v-icon>
          Guardar
        </v-btn>
      </div>
    </div>

    <v-alert v-if="dirty" dense text type="warning" class="mb-4">
      Hay cambios de secuencia sin guardar.
    </v-alert>
    <v-alert v-if="error" dense text type="error" class="mb-4">
      {{ error }}
    </v-alert>

    <div class="kpi-row">
      <div class="kpi">
        <div class="kpi-value">{{ kpis.pendingM2 | number('0,0.0') }}</div>
        <div class="kpi-label">m² pendientes</div>
      </div>
      <div class="kpi">
        <div class="kpi-value">{{ kpis.pendingQty | number('0,0') }}</div>
        <div class="kpi-label">unidades pendientes</div>
      </div>
      <div class="kpi kpi-late">
        <div class="kpi-value">{{ kpis.late }}</div>
        <div class="kpi-label">OT atrasadas</div>
      </div>
      <div class="kpi">
        <div class="kpi-value">{{ kpis.loadHours | number('0,0') }} h</div>
        <div class="kpi-label">carga estimada</div>
      </div>
      <div class="kpi">
        <div class="kpi-value">{{ kpis.machinesWithQueue }}/{{ kpis.machineCount }}</div>
        <div class="kpi-label">máquinas con cola</div>
      </div>
    </div>

    <div class="command-bar">
      <v-text-field
        v-model="search"
        placeholder="Buscar OT, cliente, producto o SKU"
        prepend-inner-icon="fas fa-search"
        outlined dense hide-details clearable
        class="command-search"
        :disabled="loading"
      />
      <v-select
        v-model="areaFilter"
        :items="areaOptions"
        outlined dense hide-details
        class="command-select-wide"
        :disabled="loading"
      />
      <v-select
        v-model="riskFilter"
        :items="riskOptions"
        outlined dense hide-details
        class="command-select"
        :disabled="loading"
      />
      <v-select
        v-model="equipmentFilter"
        :items="equipmentOptions"
        outlined dense hide-details
        class="command-select"
        :disabled="loading"
      />
      <v-select
        v-if="view === 'cola' && !areaFilter"
        v-model="selectedMachineId"
        :items="machineOptions"
        outlined dense hide-details
        class="command-select-wide"
        :disabled="loading || !visibleMachines.length"
      />
      <v-btn-toggle v-if="view === 'gantt'" v-model="zoom" mandatory dense class="zoom-toggle">
        <v-btn :value="1" small>Hoy</v-btn>
        <v-btn :value="7" small>7 días</v-btn>
        <v-btn :value="14" small>14 días</v-btn>
      </v-btn-toggle>
      <v-btn-toggle v-model="view" mandatory dense class="view-toggle">
        <v-btn value="gantt" small>
          <v-icon left x-small>fas fa-project-diagram</v-icon>
          Gantt
        </v-btn>
        <v-btn value="cola" small>
          <v-icon left x-small>fas fa-list-ol</v-icon>
          Cola
        </v-btn>
      </v-btn-toggle>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="accent" height="3" class="mb-3" />

    <plant-gantt
      v-if="view === 'gantt' && !loading"
      :machines="visibleMachines"
      :search="search"
      :risk-filter="riskFilter"
      :equipment-filter="equipmentFilter"
      :zoom="zoom"
      @open-work-order="displayWorkOrder"
      @move-process="moveProcess"
      @assign-equipment="assignEquipment"
    />

    <div v-else-if="view === 'cola' && selectedMachine" class="cola-wrap">
      <machine-planner-alone
        :machine-data="selectedMachine"
        :drag-enable="true"
        :hide-chrome="true"
        :search="search"
        :filter-equipment="equipmentFilter"
        :risk-filter="riskFilter"
        @queue-change="onQueueChange"
        @refresh="fetchPlant"
        @open-work-order="displayWorkOrder"
        @assign-equipment="assignEquipment"
      />
    </div>

    <div v-else-if="!loading" class="planner-empty">
      <v-icon size="48" color="primary" class="mb-3">fas fa-industry</v-icon>
      <div class="text-h6 font-weight-bold">No hay áreas para planificar</div>
    </div>

    <v-dialog v-model="showWorkOrder" max-width="1200" persistent scrollable>
      <v-card>
        <work-order-card
          :work_order="workOrderDisplay"
          :loading="workOrderOnDisplayLoading"
          @close="showWorkOrder = false"
        />
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { getWorkOrder, updatePlanner, asignEquipment } from '@/api/production'
import { fetchPlannerPlant } from '@/api/reports'
import WorkOrderCard from '@/components/production/WorkOrderCard'
import MachinePlannerAlone from '@/components/production/MachinePlannerAlone.vue'
import PlantGantt from '@/components/production/PlantGantt.vue'
import notifications from '@/utils/notifications'
import {
  estimatePlant,
  plantKpis,
  uniqueEquipmentNames,
  buildSavePayload,
} from '@/utils/plannerEstimate'

export default {
  name: 'MainPlanner',
  components: { WorkOrderCard, MachinePlannerAlone, PlantGantt },
  data() {
    return {
      machines: [],
      loading: false,
      saving: false,
      dirty: false,
      error: '',
      search: '',
      riskFilter: 'all',
      equipmentFilter: 'Todos',
      areaFilter: '',
      zoom: 7,
      view: 'gantt',
      selectedMachineId: null,
      showWorkOrder: false,
      workOrderDisplay: {},
      workOrderOnDisplayLoading: false,
      riskOptions: [
        { text: 'Todas las OT', value: 'all' },
        { text: 'En riesgo', value: 'risk' },
        { text: 'Atrasadas', value: 'late' },
      ],
    }
  },
  computed: {
    visibleMachines() {
      if (!this.areaFilter) return this.machines
      return this.machines.filter((machine) => machine.id === this.areaFilter)
    },
    kpis() {
      return plantKpis(this.visibleMachines)
    },
    equipmentOptions() {
      return uniqueEquipmentNames(this.visibleMachines)
    },
    areaOptions() {
      return [
        { text: 'Todas las áreas', value: '' },
        ...this.machines.map((machine) => ({
          text: machine.name,
          value: machine.id,
        })),
      ]
    },
    machineOptions() {
      return this.visibleMachines.map((machine) => ({
        text: machine.name,
        value: machine.id,
      }))
    },
    selectedMachine() {
      return this.machines.find((machine) => machine.id === this.selectedMachineId) || null
    },
  },
  created() {
    this.fetchPlant()
  },
  methods: {
    async fetchPlant() {
      this.loading = true
      this.error = ''
      try {
        const res = await fetchPlannerPlant()
        this.machines = estimatePlant(res || [])
        if (!this.selectedMachineId && this.machines.length) {
          this.selectedMachineId = this.machines[0].id
        }
        if (this.areaFilter && !this.machines.some((machine) => machine.id === this.areaFilter)) {
          this.areaFilter = ''
        }
        this.dirty = false
      } catch (e) {
        this.error = 'No se pudo cargar el planificador de planta.'
        notifications.showError('Error al cargar el planificador: ' + e)
      } finally {
        this.loading = false
      }
    },
    async savePlant() {
      this.saving = true
      try {
        await updatePlanner(buildSavePayload(this.machines))
        notifications.showSuccess('Planificación de planta guardada')
        await this.fetchPlant()
      } catch (e) {
        notifications.showError('Error al guardar: ' + e)
      } finally {
        this.saving = false
      }
    },
    moveProcess({ processId, fromMachineId, toMachineId, insertAt }) {
      const fromMachine = this.machines.find((machine) => machine.id === fromMachineId)
      const toMachine = this.machines.find((machine) => machine.id === toMachineId)
      if (!fromMachine || !toMachine) return
      const fromIndex = (fromMachine.queue || []).findIndex((item) => item.process === processId)
      if (fromIndex < 0) return
      const [item] = fromMachine.queue.splice(fromIndex, 1)
      let at = insertAt
      if (fromMachineId === toMachineId && fromIndex < at) at -= 1
      at = Math.max(0, Math.min(at, toMachine.queue.length))
      toMachine.queue.splice(at, 0, item)
      estimatePlant(this.machines)
      this.dirty = true
    },
    onQueueChange() {
      estimatePlant(this.machines)
      this.dirty = true
    },
    async assignEquipment({ process, equipmentId, equipmentName }) {
      try {
        await asignEquipment({
          processes: [{ process: process.process }],
          equipment: equipmentId,
        })
        process.equipment_assigned = equipmentName || ''
        estimatePlant(this.machines)
        notifications.showSuccess(
          equipmentName
            ? 'OT ' + process.work_order + ' → ' + equipmentName
            : 'OT ' + process.work_order + ' quedó sin máquina'
        )
      } catch (e) {
        notifications.showError('No se pudo asignar la máquina: ' + e)
      }
    },
    async displayWorkOrder(item) {
      const woId = item && (item.work_order || item.work_order_id)
      if (!woId) return
      this.workOrderOnDisplayLoading = true
      this.showWorkOrder = true
      try {
        this.workOrderDisplay = await getWorkOrder(woId)
      } catch (e) {
        notifications.showError('Error al cargar OT: ' + e)
        this.showWorkOrder = false
      } finally {
        this.workOrderOnDisplayLoading = false
      }
    },
  },
  watch: {
    areaFilter(value) {
      this.equipmentFilter = 'Todos'
      if (value && this.view === 'cola') this.selectedMachineId = value
    },
    view(value) {
      if (value === 'cola' && this.areaFilter) this.selectedMachineId = this.areaFilter
    },
  },
}
</script>

<style scoped>
.plant-planner {
  max-width: 1600px;
  padding-top: 8px;
}
.planner-top {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
.planner-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #1a1f2e;
  line-height: 1.15;
}
.planner-subtitle {
  margin: 6px 0 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
}
.planner-actions {
  display: flex;
  gap: 8px;
}
.kpi-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}
.kpi {
  background: #fff;
  border: 1px solid rgba(26, 31, 46, 0.08);
  border-radius: 10px;
  padding: 12px 14px;
}
.kpi-value {
  font-size: 1.25rem;
  font-weight: 800;
  color: #1a1f2e;
  letter-spacing: -0.03em;
}
.kpi-late .kpi-value { color: #b91c1c; }
.kpi-label {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
}
.command-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.command-search {
  flex: 1 1 260px;
  min-width: 220px;
}
.command-select { width: 160px; }
.command-select-wide { width: 220px; }
.zoom-toggle,
.view-toggle {
  background: #fff;
  border: 1px solid rgba(26, 31, 46, 0.08);
}
.cola-wrap {
  background: #fff;
  border: 1px solid rgba(26, 31, 46, 0.08);
  border-radius: 12px;
  overflow: hidden;
}
.planner-empty {
  text-align: center;
  padding: 72px 24px;
  background: #fff;
  border: 1px dashed rgba(26, 31, 46, 0.12);
  border-radius: 12px;
}
@media (max-width: 960px) {
  .kpi-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .planner-top { flex-direction: column; align-items: stretch; }
}
</style>
